import React, { useState, useRef } from 'react';
import { Clock, X, AlertTriangle, Flag } from 'lucide-react';
import { useNotifications, REMINDER_OFFSETS } from '../../context/NotificationContext';
import './Toast.css';

function ToastItem({ notification, onDismiss }) {
  const [swiping, setSwiping] = useState(false);
  const [offsetX, setOffsetX] = useState(0);
  const startX = useRef(0);
  const currentX = useRef(0);

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    setSwiping(true);
  };

  const handleMouseDown = (e) => {
    startX.current = e.clientX;
    setSwiping(true);

    const handleMouseMove = (ev) => {
      const dx = ev.clientX - startX.current;
      if (dx > 0) {
        currentX.current = dx;
        setOffsetX(dx);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setSwiping(false);
      if (currentX.current > 100) {
        setOffsetX(400);
        setTimeout(() => onDismiss(notification.id), 200);
      } else {
        currentX.current = 0;
        setOffsetX(0);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchMove = (e) => {
    const dx = e.touches[0].clientX - startX.current;
    if (dx > 0) {
      currentX.current = dx;
      setOffsetX(dx);
    }
  };

  const handleTouchEnd = () => {
    setSwiping(false);
    if (currentX.current > 100) {
      setOffsetX(400);
      setTimeout(() => onDismiss(notification.id), 200);
    } else {
      currentX.current = 0;
      setOffsetX(0);
    }
  };

  const isDeadline = notification.type === 'deadline';
  const offsetLabel = REMINDER_OFFSETS[notification.reminderOffset]?.label || '';
  const priorityClass = notification.priority
    ? `toast__priority toast__priority--${notification.priority.toLowerCase()}`
    : '';
  const daysLabel = notification.daysUntil === 1 ? '1 day left' : `${notification.daysUntil} days left`;

  return (
    <div
      className={`toast ${isDeadline ? 'toast--deadline' : ''} ${swiping ? 'toast--swiping' : ''}`}
      style={{
        transform: `translateX(${offsetX}px)`,
        opacity: offsetX > 100 ? 1 - (offsetX - 100) / 300 : 1,
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
    >
      <div className="toast__icon-wrapper">
        {isDeadline ? (
          <AlertTriangle size={18} strokeWidth={2} />
        ) : (
          <Clock size={18} strokeWidth={2} />
        )}
      </div>

      <div className="toast__content">
        <div className="toast__header-row">
          <span className="toast__task-text">
            {isDeadline ? 'Application deadline soon' : notification.taskText}
          </span>
          <button
            className="toast__close"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(notification.id);
            }}
            aria-label="Dismiss notification"
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>

        <div className="toast__details">
          <span className="toast__college">{notification.collegeName}</span>
          {isDeadline ? (
            <span className="toast__deadline-badge">{daysLabel}</span>
          ) : notification.priority && (
            <span className={priorityClass}>
              <Flag size={10} />
              {notification.priority}
            </span>
          )}
          {!isDeadline && (
            <span className="toast__offset">
              <AlertTriangle size={10} />
              {offsetLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ToastContainer({ sidebarOpen = false }) {
  const { toasts, dismissToast } = useNotifications();

  if (toasts.length === 0) return null;

  return (
    <div className={`toast-container ${sidebarOpen ? 'toast-container--sidebar-open' : ''}`} aria-live="polite">
      {toasts.map((notification) => (
        <ToastItem
          key={notification.id}
          notification={notification}
          onDismiss={dismissToast}
        />
      ))}
    </div>
  );
}
